import { Dashboardv99990101Api } from '@sequencehq/api/dashboard/v99990101'
import { nodeDefinitions } from 'Automations/nodeDefinitions'
import { parseTaskReferenceName } from 'Automations/nodeDefinitions/conductor'
import { NodeType } from 'Automations/nodeDefinitions/types'
import { flow, map, groupBy, omit, filter } from 'lodash/fp'

type Task = Dashboardv99990101Api.Automations.AutomationExecutionTask
type TriggerType = Dashboardv99990101Api.Automations.TriggerType

type ParsedTask = Task & {
  nodeId: string
  nodeType: string
  taskName: string
  iteration: number
}

/**
 * When there are no tasks associated with a given start node, render
 * the start node first with an empty tasks array.
 */
const triggersWithNoStartNodeTasks: Partial<
  Record<TriggerType, keyof typeof nodeDefinitions>
> = {
  CONTRACT_UPLOADED: 'START_CONTRACT_UPLOADED'
}

/**
 * Conductor re-runs tasks inside DO_WHILE loops with `__N` iteration
 * suffixes on their reference names. Keep only the latest iteration of
 * each task (the parsed `taskName` is suffix-free, so iterations of the
 * same task share it), preserving the position each task first appeared.
 */
const dedupeToLatestIteration = (tasks: ParsedTask[]): ParsedTask[] =>
  Object.values(
    tasks.reduce<Record<string, ParsedTask>>((latestByTaskName, task) => {
      const current = latestByTaskName[task.taskName]
      if (current && current.iteration >= task.iteration) {
        return latestByTaskName
      }
      return { ...latestByTaskName, [task.taskName]: task }
    }, {})
  )

export const getTasksGroupedByNode = (
  execution: Dashboardv99990101Api.Automations.AutomationExecution
): Array<{
  nodeId: string
  nodeType: NodeType
  tasks: Array<Task>
}> => {
  const startNodeWithNoTask =
    execution.input.triggerType &&
    triggersWithNoStartNodeTasks[execution.input.triggerType]

  const startNode = startNodeWithNoTask
    ? [
        {
          nodeId: null,
          nodeType: startNodeWithNoTask,
          tasks: []
        }
      ]
    : []

  return [
    ...startNode,
    ...flow(
      map((task: Task) => ({
        ...task,
        ...parseTaskReferenceName(task.taskReferenceName)
      })),
      groupBy('nodeId'),
      map((tasksForNode: ParsedTask[]) => ({
        nodeId: tasksForNode[0].nodeId,
        nodeType: tasksForNode[0].nodeType,
        tasks: dedupeToLatestIteration(tasksForNode).map(
          omit(['nodeId', 'nodeType', 'taskName', 'iteration'])
        )
      })),
      /**
       * Exclude end nodes from the execution summary for now.
       */
      filter(node => node.nodeType !== 'END')
    )(execution.tasks)
  ]
}
